import type { ChatMessage } from "@albertine/types/openapi/concierge-v1";
import { subDays } from "date-fns/subDays";
import { subHours } from "date-fns/subHours";

export type MockChatParticipant = {
    id: string;
    name: string;
};

export type MockChatRequest = {
    id: string;
    title: string;
    status: "open" | "confirmed" | "closed";
    agent: MockChatParticipant;
    member: MockChatParticipant;
};

export type ChatMessageDisplayContext = {
    agentName: string;
    memberName: string;
    topic?: string;
};

export const DEFAULT_MOCK_REQUEST_ID = "weekend-helsinki";

const mockAgent = {
    id: "mock-agent",
    name: "Agent Bond",
} satisfies MockChatParticipant;

const mockMember = {
    id: "mock-member",
    name: "Karl Von Random",
} satisfies MockChatParticipant;

export const GENERAL_CHAT_DISPLAY: ChatMessageDisplayContext = {
    agentName: mockAgent.name,
    memberName: mockMember.name,
};

const mockRequests = {
    "weekend-helsinki": {
        id: "weekend-helsinki",
        title: "Weekend in Helsinki",
        status: "open",
        agent: mockAgent,
        member: mockMember,
    },
    "airport-transfer": {
        id: "airport-transfer",
        title: "Airport transfer",
        status: "confirmed",
        agent: {
            id: "mock-agent-elise",
            name: "Elise Moreau",
        },
        member: mockMember,
    },
} satisfies Record<string, MockChatRequest>;

const emptyBookingFields = {
    requestId: null,
    requestTitle: null,
    proposal: null,
    confirmedBookings: [],
    reply: null,
} as const satisfies Pick<
    ChatMessage,
    "requestId" | "requestTitle" | "proposal" | "confirmedBookings" | "reply"
>;

const requestThreadTemplates = {
    "weekend-helsinki": [
        {
            id: "req-agent-catalog",
            messageHtml:
                "<p>These two places match what you described for Saturday.</p>",
            attachments: [
                {
                    type: "catalog-item",
                    id: "catalog-palace",
                    name: "Palace",
                    keyDetail: "Modern Nordic tasting menu",
                    imageUrl:
                        "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4",
                },
                {
                    type: "catalog-item",
                    id: "catalog-savoy",
                    name: "Savoy",
                    keyDetail: "Classic Finnish dining",
                    imageUrl:
                        "https://images.unsplash.com/photo-1559339352-11d035aa65de",
                },
            ],
            sender: {
                type: "agent",
                agentId: mockAgent.id,
                name: mockAgent.name,
                avatarUrl: null,
            },
            memberReaction: null,
            ...emptyBookingFields,
            requestId: "weekend-helsinki",
            requestTitle: "Weekend in Helsinki",
        },
        {
            id: "req-agent-reply",
            messageHtml:
                "<p>I found a few options for your weekend in Helsinki. What would you like to explore first?</p>",
            attachments: [],
            sender: {
                type: "agent",
                agentId: mockAgent.id,
                name: mockAgent.name,
                avatarUrl: null,
            },
            memberReaction: "👍",
            ...emptyBookingFields,
            requestId: "weekend-helsinki",
            requestTitle: "Weekend in Helsinki",
        },
        {
            id: "req-member-text",
            messageHtml: "<p>Please find somewhere special for dinner on Saturday.</p>",
            attachments: [],
            sender: { type: "member", memberId: mockMember.id, name: mockMember.name },
            memberReaction: null,
            ...emptyBookingFields,
            requestId: "weekend-helsinki",
            requestTitle: "Weekend in Helsinki",
        },
    ],
    "airport-transfer": [
        {
            id: "req-agent-confirm",
            messageHtml:
                "<p>Your driver will meet you at arrivals with a name sign.</p>",
            attachments: [],
            sender: {
                type: "agent",
                agentId: "mock-agent-elise",
                name: "Elise Moreau",
                avatarUrl: null,
            },
            memberReaction: null,
            ...emptyBookingFields,
            requestId: "airport-transfer",
            requestTitle: "Airport transfer",
        },
        {
            id: "req-member-transfer",
            messageHtml: "<p>Can you arrange a car from HEL to the hotel at 16:30?</p>",
            attachments: [],
            sender: { type: "member", memberId: mockMember.id, name: mockMember.name },
            memberReaction: null,
            ...emptyBookingFields,
            requestId: "airport-transfer",
            requestTitle: "Airport transfer",
        },
    ],
} satisfies Record<keyof typeof mockRequests, Omit<ChatMessage, "sentAt">[]>;

export function listMockRequestIds(): string[] {
    return Object.keys(mockRequests);
}

export function getMockRequest(requestId: string): MockChatRequest | null {
    return mockRequests[requestId as keyof typeof mockRequests] ?? null;
}

export function toDisplayContext(
    request: MockChatRequest | null,
): ChatMessageDisplayContext {
    if (!request) {
        return GENERAL_CHAT_DISPLAY;
    }

    return {
        agentName: request.agent.name,
        memberName: request.member.name,
        topic: request.title,
    };
}

export async function fetchMockRequest(requestId: string): Promise<MockChatRequest> {
    await wait(80);
    return (
        getMockRequest(requestId) ?? {
            id: requestId,
            title: `Request ${requestId}`,
            status: "open",
            agent: mockAgent,
            member: mockMember,
        }
    );
}

export async function fetchMockRequestMessages(
    requestId: string,
): Promise<ChatMessage[]> {
    await wait(120);
    const templates =
        requestThreadTemplates[requestId as keyof typeof requestThreadTemplates];
    return templates ? mintMessages(templates) : [];
}

function mintMessages(templates: Omit<ChatMessage, "sentAt">[]): ChatMessage[] {
    const now = new Date();
    return templates.map((template, index) => ({
        ...template,
        attachments: template.attachments.map((attachment) => ({ ...attachment })),
        sender: { ...template.sender },
        sentAt: subHours(subDays(now, index), 2).toISOString(),
    }));
}

function wait(ms: number): Promise<void> {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}
